// Report dos tipos padrão — Entrevista (1), Parceria (3), Negociação (4) e as etapas do recrutamento de PN (5 Entrevista
// individual, 6 Dinâmica, 7 Pré Job Sample, 8 Aprovação final) — no fluxo novo: página / central de pendências,
// rascunho no servidor e finalização idempotente no MESMO `POST /api/appointments/{id}/report/finalize` do Momento X.
//
// As perguntas, opções, textos e condições são os de SEMPRE: os da antiga `AppointmentReportSidebar.vue` (o formulário
// que o PN já conhece). Por isso o report grava `report_version = 1` e o rascunho `form_version = 1`; o Momento X do CX
// é o `report_version = 2` (`appointment_report_v2.js`). Todos os textos daqui são [app] — já existiam na sidebar; o
// que for texto NOVO fica em `NEW_COPY` (`appointment_report_v2.js`), para o CX revisar num lugar só.
//
// A matriz de obrigatórios abaixo é a MESMA que o `FinalizeStandardReportRequest` do backend-core valida (e a mesma das
// `validations()` da sidebar). Muda uma, muda a outra.
import moment from 'moment'
import { APP_COPY, NEW_COPY } from '@/constants/appointment_report_v2'
import {
  notClosedWhyOptionsForCustomer,
  notClosedWhyOptionsForPN,
  notHadWhyOptions,
} from '@/constants/appointment_report_options'
import {
  EA,
  EA_PN,
  EC_PN,
  ED_PN,
  EF_PN,
  ER_PN,
  MXA,
  NA,
  PA,
} from '@/constants/appointment_status'
import {
  INDIVIDUAL_INTERVIEW,
  INTERVIEW,
  PARTNERSHIP,
  PPN_APPOINTMENTS,
  X_MOMENT,
} from '@/constants/appointment_types'

// Versão do formulário (perguntas de sempre) — `form_version` do rascunho e `report_version` gravado pelo servidor.
export const STANDARD_FORM_VERSION = 1

// ---------------------------------------------------------------------------------------------------------------
// Respostas: os nomes do corpo do finalize (os do extinto `POST /api/appointments/report`) + os campos do
// reagendamento, que só existem no rascunho (o reagendamento é um `POST /api/appointments` à parte, como na sidebar).
// ---------------------------------------------------------------------------------------------------------------
export const STANDARD_FIELDS = Object.freeze({
  HAD_MEETING: 'had_meeting',
  CLOSED_DEAL: 'closed_deal',
  HAS_APPROVAL: 'has_approval',
  WHY: 'why',
  RESCHEDULE: 'reschedule_meeting',
  MEETING_START: 'meeting_start',
  MEETING_END: 'meeting_end',
  MEETING_FORMAT: 'meeting_format',
  ROOM_LINK: 'meeting_room_link',
  FEEDBACK: 'feedback',
  HAD_SUPPORT: 'had_specialist_support',
  SPECIALIST_ID: 'consultant_specialist_id',
  SPECIALIST_SCORE: 'consultant_specialist_score',
  HIGHLIGHTS: 'qualitative_highlights',
})

const F = STANDARD_FIELDS

// "Selecione até 4" do componente de estrelas da sidebar (= `FinalizeStandardReportRequest::MAX_QUALITATIVE_HIGHLIGHTS`).
export const MAX_QUALITATIVE_HIGHLIGHTS = 4

export const SPECIALIST_SCORES = [1, 2, 3, 4, 5]

// Formato presencial — grafia do backend (`AppointmentMeetingFormatTypes::IN_PEARSON`).
export const IN_PERSON_FORMAT = 'in_pearson'

// Formato do flatpickr da sidebar ("d/m/Y H:i") no moment.
export const PICKER_FORMAT = 'DD/MM/YYYY HH:mm'

// Status "Agendado" que recebem rascunho e finalização: os quatro que geram pendência + o do recrutamento de PN
// (`AppointmentReportFlowController::REPORTABLE_STATUSES`). O recrutamento recebe report, mas nunca trava.
export const STANDARD_REPORTABLE_STATUSES = [EA, NA, PA, MXA, EA_PN]

// Status do recrutamento de PN (agendado, realizado, cancelado, remarcado, excluído).
const PN_RECRUITMENT_STATUSES = [EA_PN, EF_PN, EC_PN, ER_PN, ED_PN]

// ---------------------------------------------------------------------------------------------------------------
// [app] Textos da antiga AppointmentReportSidebar (verbatim — inclusive as grafias de hoje)
// ---------------------------------------------------------------------------------------------------------------
export const STANDARD_COPY = {
  hadMeeting: 'Houve reunião?',
  hadMeetingPn: 'Houve reunião com o PN?',
  closedDeal: 'Fechou negócio?',
  hasApproval: 'Foi aprovado?',
  why: 'Qual o motivo?',
  reschedule: 'Deseja remarcar a reunião?',
  meetingStart: 'Data de início',
  meetingEnd: 'Data de término',
  meetingFormat: 'Formato da reunião',
  meetingFormatPlaceholder: 'Selecione o formato',
  meetingAddress: 'Endereço da reunião',
  meetingLink: 'Link da reunião',
  feedback: 'Comentários do report',
  specialistSection: 'Avalie o PN Especialista',
  specialistInfo: 'Avalie o Planejador Especialista desse compromisso de acordo com um conjunto de fatores, '
    + 'em destaque pela qualidade do atendimento, nível de preparo, pontualidade e domínio dos conceitos.',
  specialistInfoLabel: 'Informações sobre a avaliação do PN Especialista',
  hadSupport: 'Houve um especialista suporte lhe auxiliando?',
  specialist: 'Especialista suporte',
  score: 'Dê uma nota de 1 a 5',
  // Do antigo componente de estrelas da sidebar (StarRating.vue).
  highlights: max => `O que destacou na avaliação? (selecione até ${max})`,
  highlightsSummary: 'O que destacou na avaliação?',
  highlightsCount: (count, max) => `${count} / ${max} selecionados`,
  highlightsLoading: 'Carregando opções...',
  highlightsEmpty: 'Nenhuma opção disponível para esta nota',
  // Pós-report: Parceria com negócio fechado (contrato) e entrevista individual aprovada (questionário). Os textos do
  // contrato ficam em `APP_COPY` (`contractReportQuestion`, `reportNow`, `reportLater`), já usados pelo Momento X.
  individualInterviewQuestion: 'Deseja responder o questionário da entrevista individual?',
  answerNow: 'Responder agora',
  answerLater: 'Responder depois',
  // Toasts de carregamento / reagendamento.
  loadSpecialistsError: 'Ocorreu um erro ao carregar os consultores especialistas para seleção. '
    + 'Entre em contato com o setor de TI.',
  loadMeetingFormatsError: 'Ocorreu um erro ao carregar os formatos de reunião para seleção. '
    + 'Entre em contato com o setor de TI.',
  highlightsError: 'Ocorreu um erro na exibição das tags de avaliação. Entre em contato com o setor de TI.',
  rescheduleError: 'Ocorreu um erro ao criar o compromisso. Entre em contato com o setor de TI.',
}

// [app] Mensagens de validação da sidebar. A do formato usa a mensagem genérica de escolha da sidebar ("Você deve
// escolher uma opção"): lá o formato mostrava a mensagem padrão do Vuelidate, em inglês.
export const STANDARD_ERRORS = {
  hadMeeting: 'Você deve informar se houve reunião',
  closedDeal: 'Você deve informar se o negócio foi fechado',
  hasApproval: 'Você deve informar se o PN foi aprovado',
  why: 'Você deve informar um motivo',
  reschedule: 'Você deve escolher uma opção',
  meetingStart: 'Você deve informar uma data de início',
  meetingEnd: 'Você deve informar uma data de término',
  meetingEndBeforeStart: 'Você deve informar uma data superior a de início',
  overlapping: APP_COPY.timeAlreadyBooked,
  meetingFormat: 'Você deve escolher uma opção',
  roomAddress: 'Você deve informar o endereço da reunião',
  roomLink: 'Você deve informar o link da reunião',
  invalidUrl: APP_COPY.invalidMeetingUrl,
  // Na sidebar a pergunta nunca ficava sem resposta (começa em Não); vale a mensagem genérica de escolha.
  hadSupport: 'Você deve escolher uma opção',
  specialist: 'Você deve informar o especialista suporte',
  score: 'Você deve informar uma nota',
  highlightsRequired: 'Você deve selecionar pelo menos 1 destaque',
  highlightsMax: 'Selecione no máximo 4 destaques',
}

// Validação de URL da reunião online — a MESMA regex da sidebar (e de `MEETING_URL_PATTERN` em
// `modules/appointment/mixins/newAppointmentForm.js`). Presencial não valida URL (é endereço).
const MEETING_URL_PATTERN = /^(https?:\/\/)?(www\.)?([a-z0-9]+([.-][a-z0-9]+)*\.)([a-z]{2,}|[a-z]{2,}\.[a-z]{2,})(:[0-9]{1,5})?(\/.*)?$/i

export const isValidMeetingUrl = value => typeof value === 'string' && MEETING_URL_PATTERN.test(value.trim())

// ---------------------------------------------------------------------------------------------------------------
// Contexto do compromisso (decidido pelo COMPROMISSO, nunca pelo que o PN responde)
// ---------------------------------------------------------------------------------------------------------------

/**
 * Etapa do recrutamento de PN? A sidebar olhava o status (`status_id == EA_PN`); o servidor aceita o tipo (5–8) OU o
 * status (21–25) — `AppointmentService::isPnRecruitmentAppointment`. Os dois concordam no banco; o tipo também vale
 * depois do report (o compromisso sai do 21), quando a tela mostra "já reportado".
 */
export const isPnRecruitmentAppointment = appointment => {
  if (!appointment) return false
  return PPN_APPOINTMENTS.includes(Number(appointment.event_type))
    || PN_RECRUITMENT_STATUSES.includes(Number(appointment.status_id))
}

/** Especialista do compromisso: `consultant_specialist.id` (rascunho) ou `consultant_specialist_id` (GET do compromisso). */
export const appointmentSpecialistId = appointment => {
  if (!appointment) return null
  const nested = appointment.consultant_specialist && appointment.consultant_specialist.id
  const id = Number(nested || appointment.consultant_specialist_id)

  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * O que o formulário pergunta para ESTE compromisso (as condições de exibição da sidebar):
 *  - `isPn`: "Houve reunião com o PN?" / "Foi aprovado?" e nenhuma avaliação de especialista;
 *  - `specialistBlock` ("Avalie o PN Especialista"): fora do recrutamento, quando quem reporta não é o especialista do
 *    cliente (`specialist` do GET do compromisso) e, na Entrevista, só se o compromisso tem especialista;
 *  - `asksSupport` ("Houve um especialista suporte lhe auxiliando?"): o bloco, menos na Entrevista (lá o especialista
 *    vem direto, já preenchido).
 * `options.isCustomerSpecialist` = o `specialist === 1` da sidebar (a API do rascunho não traz esse dado).
 */
export const standardReportContext = (appointment, options) => {
  const source = appointment || {}
  const eventType = Number(source.event_type) || 0
  const isPn = isPnRecruitmentAppointment(source)
  const isInterview = eventType === INTERVIEW
  const specialistId = appointmentSpecialistId(source)
  const isCustomerSpecialist = !!(options && options.isCustomerSpecialist)
  const specialistBlock = !isPn && !isCustomerSpecialist && (!isInterview || !!specialistId)

  return {
    eventType,
    isPn,
    isInterview,
    isPartnership: eventType === PARTNERSHIP,
    isIndividualInterview: eventType === INDIVIDUAL_INTERVIEW,
    isMomentX: eventType === X_MOMENT,
    appointmentSpecialistId: specialistId,
    specialistBlock,
    asksSupport: specialistBlock && !isInterview,
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Respostas vazias / vindas do rascunho
// ---------------------------------------------------------------------------------------------------------------

/**
 * Estado inicial das respostas. "Houve um especialista suporte lhe auxiliando?" começa em Não, como sempre começou na
 * sidebar (`hadSupport: "false"`); as outras perguntas começam vazias.
 */
export const emptyStandardAnswers = () => ({
  [F.HAD_MEETING]: null,
  [F.CLOSED_DEAL]: null,
  [F.HAS_APPROVAL]: null,
  [F.WHY]: null,
  [F.RESCHEDULE]: null,
  [F.MEETING_START]: null,
  [F.MEETING_END]: null,
  [F.MEETING_FORMAT]: null,
  [F.ROOM_LINK]: null,
  [F.FEEDBACK]: null,
  // Começa vazia, como no Momento X (decisão do usuário, 2026-10-01): o PN responde de propósito.
  [F.HAD_SUPPORT]: null,
  [F.SPECIALIST_ID]: null,
  [F.SPECIALIST_SCORE]: null,
  [F.HIGHLIGHTS]: [],
})

const YES_NO_FIELDS = [F.HAD_MEETING, F.CLOSED_DEAL, F.HAS_APPROVAL, F.RESCHEDULE, F.HAD_SUPPORT]
const TEXT_FIELDS = [F.WHY, F.ROOM_LINK, F.FEEDBACK]
const PICKER_FIELDS = [F.MEETING_START, F.MEETING_END]
const PICKER_VALUE = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/

const toYesNo = value => {
  if (value === true || value === 1 || value === '1' || value === 'true') return true
  if (value === false || value === 0 || value === '0' || value === 'false') return false
  return null
}

const toPositiveInt = value => {
  const number = Number(value)
  return Number.isInteger(number) && number > 0 ? number : null
}

/**
 * Respostas do rascunho → as chaves conhecidas, com os tipos garantidos. Chave estranha é descartada; motivo fora da
 * lista do caminho some no formulário (ver `isWhyValid`), porque a lista depende das outras respostas.
 */
export const sanitizeStandardAnswers = raw => {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}
  const answers = emptyStandardAnswers()
  YES_NO_FIELDS.forEach(key => {
    if (!Object.prototype.hasOwnProperty.call(source, key)) return
    const value = toYesNo(source[key])
    answers[key] = value
  })
  TEXT_FIELDS.forEach(key => {
    answers[key] = typeof source[key] === 'string' ? source[key] : null
  })
  PICKER_FIELDS.forEach(key => {
    answers[key] = typeof source[key] === 'string' && PICKER_VALUE.test(source[key]) ? source[key] : null
  })
  answers[F.MEETING_FORMAT] = typeof source[F.MEETING_FORMAT] === 'string' && source[F.MEETING_FORMAT]
    ? source[F.MEETING_FORMAT]
    : null
  answers[F.SPECIALIST_ID] = toPositiveInt(source[F.SPECIALIST_ID])
  const score = toPositiveInt(source[F.SPECIALIST_SCORE])
  answers[F.SPECIALIST_SCORE] = SPECIALIST_SCORES.includes(score) ? score : null
  answers[F.HIGHLIGHTS] = Array.isArray(source[F.HIGHLIGHTS])
    ? source[F.HIGHLIGHTS].filter(name => typeof name === 'string' && name).slice(0, MAX_QUALITATIVE_HIGHLIGHTS)
    : []

  return answers
}

/**
 * Report gravado (resumo `presentReport` do servidor) → respostas, para o "Resumo do report". Aqui o "especialista
 * suporte?" NÃO ganha o Não padrão: report antigo (feito pela sidebar, que nunca enviava a resposta) fica sem a linha.
 */
export const answersFromStandardReport = report => {
  const source = report || {}
  return {
    ...sanitizeStandardAnswers(source),
    [F.HAD_SUPPORT]: toYesNo(source[F.HAD_SUPPORT]),
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Regras (a matriz da sidebar = a do `FinalizeStandardReportRequest`)
// ---------------------------------------------------------------------------------------------------------------
const toOptions = list => list.map(option => ({ value: option.name, label: option.name }))

const NOT_HELD_WHY_OPTIONS = toOptions(notHadWhyOptions)
const NOT_CLOSED_WHY_OPTIONS = toOptions(notClosedWhyOptionsForCustomer)
const NOT_APPROVED_WHY_OPTIONS = toOptions(notClosedWhyOptionsForPN)

/** Resposta de resultado do caminho: "Fechou negócio?" (cliente) ou "Foi aprovado?" (recrutamento de PN). */
export const resultField = context => (context && context.isPn ? F.HAS_APPROVAL : F.CLOSED_DEAL)

/** "Qual o motivo?" aparece: sem reunião, não fechou ou não foi aprovado. */
export const asksWhy = (answers, context) => {
  const values = answers || {}
  if (values[F.HAD_MEETING] === false) return true
  return values[F.HAD_MEETING] === true && values[resultField(context)] === false
}

/**
 * Opções de "Qual o motivo?" (as mesmas listas e a mesma escolha da sidebar): com reunião e resultado negativo, os
 * motivos do cliente ou do candidato; sem reunião, "Cancelou" / "Não quer mais". `value` = `label` = o texto que vai
 * em `why` (timeline, exportação e BI leem o texto).
 */
export const whyOptionsFor = (answers, context) => {
  const values = answers || {}
  if (values[F.HAD_MEETING] === true && values[resultField(context)] === false) {
    return context && context.isPn ? NOT_APPROVED_WHY_OPTIONS : NOT_CLOSED_WHY_OPTIONS
  }

  return NOT_HELD_WHY_OPTIONS
}

export const isWhyValid = (answers, context) => whyOptionsFor(answers, context)
  .some(option => option.value === (answers || {})[F.WHY])

export const toPickerMoment = value => {
  if (typeof value !== 'string' || !PICKER_VALUE.test(value)) return null
  const parsed = moment(value, PICKER_FORMAT, true)
  return parsed.isValid() ? parsed : null
}

/** Término padrão ao escolher o início: +1 h (o `setEndDate` da sidebar). */
export const defaultMeetingEnd = start => {
  const parsed = toPickerMoment(start)
  return parsed ? parsed.clone().add(1, 'hour').format(PICKER_FORMAT) : null
}

/** Os dois horários do reagendamento válidos e em ordem (pré-condição da checagem de conflito de horário). */
export const hasValidRescheduleRange = answers => {
  const start = toPickerMoment(answers && answers[F.MEETING_START])
  const end = toPickerMoment(answers && answers[F.MEETING_END])
  return !!(start && end && end.isAfter(start))
}

export const asksReschedule = answers => !!answers && answers[F.HAD_MEETING] === false

export const reschedules = answers => asksReschedule(answers) && answers[F.RESCHEDULE] === true

/** O especialista (seleção + avaliação) está no caminho: Entrevista sempre; demais tipos só com "suporte = Sim". */
export const specialistOnPath = (answers, context) => {
  if (!context || !context.specialistBlock) return false
  return context.isInterview || (answers || {})[F.HAD_SUPPORT] === true
}

/** Nota + destaques aparecem quando há especialista escolhido (como o `v-if="specialistId"` da sidebar). */
export const ratingOnPath = (answers, context) => specialistOnPath(answers, context)
  && !!(answers || {})[F.SPECIALIST_ID]

/** A avaliação é obrigatória só com "suporte = Sim" (na Entrevista a pergunta não existe e nada é exigido). */
const evaluationRequired = (answers, context) => !!context && context.asksSupport
  && (answers || {})[F.HAD_SUPPORT] === true

/**
 * Itens do caminho ativo, na ordem da tela: `{ key, kind, answered, message }`. `answered = false` = falta ou está
 * inválido (a mensagem diz qual). `state.overlapping` = o início/término do reagendamento conflita com outro
 * compromisso (checagem do servidor, como na sidebar — enquanto conflitar, não finaliza). `state.now` = "agora"
 * (timestamp) para recusar reagendamento num horário que já passou — o `POST /api/appointments` também recusa (405).
 */
export const standardReportItems = (answers, context, state) => {
  const values = answers || {}
  const ctx = context || {}
  const items = []
  const push = (key, kind, answered, message) => items.push({
    key,
    kind,
    answered: !!answered,
    message: answered ? '' : message,
  })

  push(F.HAD_MEETING, 'question', toYesNo(values[F.HAD_MEETING]) !== null, STANDARD_ERRORS.hadMeeting)

  if (values[F.HAD_MEETING] === true) {
    const field = resultField(ctx)
    push(
      field,
      'question',
      toYesNo(values[field]) !== null,
      ctx.isPn ? STANDARD_ERRORS.hasApproval : STANDARD_ERRORS.closedDeal,
    )
  }

  if (asksWhy(values, ctx)) push(F.WHY, 'select', isWhyValid(values, ctx), STANDARD_ERRORS.why)

  if (asksReschedule(values)) {
    push(F.RESCHEDULE, 'question', toYesNo(values[F.RESCHEDULE]) !== null, STANDARD_ERRORS.reschedule)
  }

  if (reschedules(values)) {
    const start = toPickerMoment(values[F.MEETING_START])
    const end = toPickerMoment(values[F.MEETING_END])
    const overlapping = !!(state && state.overlapping)
    const now = state && Number.isFinite(state.now) ? moment(state.now).startOf('minute') : null
    let startMessage = ''
    if (!start) startMessage = STANDARD_ERRORS.meetingStart
    else if (now && start.isBefore(now)) startMessage = NEW_COPY.startInPast
    else if (overlapping) startMessage = STANDARD_ERRORS.overlapping
    push(F.MEETING_START, 'field', !startMessage, startMessage)

    let endMessage = ''
    if (!end) endMessage = STANDARD_ERRORS.meetingEnd
    else if (start && !end.isAfter(start)) endMessage = STANDARD_ERRORS.meetingEndBeforeStart
    push(F.MEETING_END, 'field', !endMessage, endMessage)

    push(F.MEETING_FORMAT, 'select', !!values[F.MEETING_FORMAT], STANDARD_ERRORS.meetingFormat)

    const inPerson = values[F.MEETING_FORMAT] === IN_PERSON_FORMAT
    const link = typeof values[F.ROOM_LINK] === 'string' ? values[F.ROOM_LINK].trim() : ''
    let linkMessage = ''
    if (!link) linkMessage = inPerson ? STANDARD_ERRORS.roomAddress : STANDARD_ERRORS.roomLink
    else if (!inPerson && !isValidMeetingUrl(link)) linkMessage = STANDARD_ERRORS.invalidUrl
    push(F.ROOM_LINK, 'field', !linkMessage, linkMessage)
  }

  if (ctx.asksSupport) {
    push(F.HAD_SUPPORT, 'question', toYesNo(values[F.HAD_SUPPORT]) !== null, STANDARD_ERRORS.hadSupport)
  }

  if (evaluationRequired(values, ctx)) {
    push(F.SPECIALIST_ID, 'select', !!values[F.SPECIALIST_ID], STANDARD_ERRORS.specialist)
    // Nota e destaques só contam quando aparecem (com o especialista escolhido) — o contador da barra nunca aponta
    // para um campo que ainda não está na tela.
    if (values[F.SPECIALIST_ID]) {
      push(F.SPECIALIST_SCORE, 'rating', !!values[F.SPECIALIST_SCORE], STANDARD_ERRORS.score)
      if (values[F.SPECIALIST_SCORE] > 0) {
        const count = Array.isArray(values[F.HIGHLIGHTS]) ? values[F.HIGHLIGHTS].length : 0
        push(
          F.HIGHLIGHTS,
          'highlights',
          count >= 1 && count <= MAX_QUALITATIVE_HIGHLIGHTS,
          count > MAX_QUALITATIVE_HIGHLIGHTS ? STANDARD_ERRORS.highlightsMax : STANDARD_ERRORS.highlightsRequired,
        )
      }
    }
  }

  return items
}

export const missingStandardItems = (answers, context, state) => standardReportItems(answers, context, state)
  .filter(item => !item.answered)

/**
 * Corpo do finalize (contrato do `FinalizeStandardReportRequest`): só o caminho ativo, com tipos JSON de verdade.
 *  - `had_specialist_support` vai sempre em Parceria/Negociação (o servidor exige): quando o bloco não aparece (quem
 *    reporta é o especialista do cliente), vai o Não padrão da sidebar;
 *  - Entrevista: o especialista do compromisso vai mesmo com o bloco escondido — a sidebar sempre o mandava;
 *  - recrutamento de PN e "suporte = Não": nada de especialista. O servidor grava sozinho o especialista do compromisso
 *    nesses casos (o mesmo que a sidebar mandava), lido do compromisso e nunca do corpo.
 */
export const buildStandardFinalizePayload = (answers, context) => {
  const values = answers || {}
  const ctx = context || {}
  const body = { [F.HAD_MEETING]: values[F.HAD_MEETING] === true }

  if (body[F.HAD_MEETING]) {
    const field = resultField(ctx)
    body[field] = values[field] === true
  }
  if (asksWhy(values, ctx)) body[F.WHY] = values[F.WHY]

  const feedback = typeof values[F.FEEDBACK] === 'string' ? values[F.FEEDBACK].trim() : ''
  body[F.FEEDBACK] = feedback || null

  if (!ctx.isPn && !ctx.isInterview) {
    body[F.HAD_SUPPORT] = ctx.asksSupport ? values[F.HAD_SUPPORT] === true : false
  }

  const specialistId = toPositiveInt(values[F.SPECIALIST_ID])
  const sendsSpecialist = !ctx.isPn && !!specialistId
    && (ctx.isInterview || (ctx.asksSupport && values[F.HAD_SUPPORT] === true))
  if (sendsSpecialist) {
    body[F.SPECIALIST_ID] = specialistId
    if (ratingOnPath(values, ctx) && values[F.SPECIALIST_SCORE]) {
      body[F.SPECIALIST_SCORE] = values[F.SPECIALIST_SCORE]
      const highlights = Array.isArray(values[F.HIGHLIGHTS]) ? values[F.HIGHLIGHTS] : []
      if (highlights.length) body[F.HIGHLIGHTS] = highlights.slice(0, MAX_QUALITATIVE_HIGHLIGHTS)
    }
  }

  return body
}

/** Campos do reagendamento → corpo do `POST /api/appointments` (os mesmos campos que a sidebar mandava). */
export const buildReschedulePayload = (answers, appointment, toDateTime) => {
  const values = answers || {}
  const source = appointment || {}
  const convert = typeof toDateTime === 'function' ? toDateTime : value => value
  const consultantId = (source.consultant && source.consultant.id) || source.consultant_id
  const customerId = (source.customer && source.customer.id) || source.customer_id

  return {
    appointment_start: convert(values[F.MEETING_START]),
    appointment_end: convert(values[F.MEETING_END]),
    consultant_id: consultantId,
    customer_id: customerId,
    event_type: source.event_type,
    meeting_format: values[F.MEETING_FORMAT],
    meeting_room_link: values[F.ROOM_LINK],
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Rótulos e resumo
// ---------------------------------------------------------------------------------------------------------------
export const standardQuestionLabel = (key, context) => {
  const ctx = context || {}
  switch (key) {
    case F.HAD_MEETING: return ctx.isPn ? STANDARD_COPY.hadMeetingPn : STANDARD_COPY.hadMeeting
    case F.CLOSED_DEAL: return STANDARD_COPY.closedDeal
    case F.HAS_APPROVAL: return STANDARD_COPY.hasApproval
    case F.WHY: return STANDARD_COPY.why
    case F.RESCHEDULE: return STANDARD_COPY.reschedule
    case F.MEETING_START: return STANDARD_COPY.meetingStart
    case F.MEETING_END: return STANDARD_COPY.meetingEnd
    case F.MEETING_FORMAT: return STANDARD_COPY.meetingFormat
    case F.FEEDBACK: return STANDARD_COPY.feedback
    case F.HAD_SUPPORT: return STANDARD_COPY.hadSupport
    case F.SPECIALIST_ID: return STANDARD_COPY.specialist
    case F.SPECIALIST_SCORE: return STANDARD_COPY.score
    case F.HIGHLIGHTS: return STANDARD_COPY.highlightsSummary
    default: return ''
  }
}

const yesNoLabel = value => {
  if (value === true) return 'Sim'
  if (value === false) return 'Não'
  return ''
}

/**
 * Linhas do "Resumo do report" (`ReportSuccess` → `summary`): `{ key, label, value, sub?, block? }` do que foi
 * respondido, com os rótulos das perguntas de sempre. `block` = texto livre (os comentários), que o resumo mostra
 * embaixo do rótulo, e não ao lado. `source` = respostas do formulário ou do report do servidor
 * (`answersFromStandardReport`). `options.specialistName(id)` resolve o nome do especialista.
 * Também serve à central, para o resumo dos tipos padrão (o do Momento X usa as perguntas do CX).
 */
export const standardReportSummaryLines = (source, context, options) => {
  const values = source || {}
  const ctx = context || {}
  const specialistName = options && typeof options.specialistName === 'function' ? options.specialistName : () => ''
  const lines = []
  const push = (key, value, sub, block) => {
    if (value === '' || value === null || value === undefined) return
    lines.push({
      key,
      label: standardQuestionLabel(key, ctx),
      value: String(value),
      sub: !!sub,
      block: !!block,
    })
  }

  push(F.HAD_MEETING, yesNoLabel(values[F.HAD_MEETING]))
  if (values[F.HAD_MEETING] === true) {
    const field = resultField(ctx)
    push(field, yesNoLabel(values[field]))
  }
  if (typeof values[F.WHY] === 'string' && values[F.WHY].trim()) push(F.WHY, values[F.WHY].trim(), true)
  if (typeof values[F.FEEDBACK] === 'string' && values[F.FEEDBACK].trim()) {
    push(F.FEEDBACK, values[F.FEEDBACK].trim(), false, true)
  }
  if (!ctx.isPn && !ctx.isInterview && ctx.asksSupport) push(F.HAD_SUPPORT, yesNoLabel(values[F.HAD_SUPPORT]))
  // O resumo mostra RESPOSTAS: o especialista só entra quando "Especialista suporte" foi perguntado e respondido. Com
  // "suporte = Não": o especialista não recebe crédito (o servidor grava null, como no Momento X). No recrutamento de
  // PN o servidor grava o especialista do compromisso (como no master), mas isso não é resposta do PN. Report antigo
  // (sem a resposta de suporte) mostra o que está gravado.
  const specialistRecorded = !ctx.isPn && (ctx.isInterview || values[F.HAD_SUPPORT] !== false)
  if (specialistRecorded && values[F.SPECIALIST_ID]) {
    push(F.SPECIALIST_ID, specialistName(values[F.SPECIALIST_ID]) || '')
    if (values[F.SPECIALIST_SCORE]) push(F.SPECIALIST_SCORE, String(values[F.SPECIALIST_SCORE]))
    const highlights = Array.isArray(values[F.HIGHLIGHTS]) ? values[F.HIGHLIGHTS] : []
    if (highlights.length) push(F.HIGHLIGHTS, highlights.join(', '))
  }

  return lines
}
